import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND, addMonthsYmd, localParts, monthRange, yearRange } from '@rydres/shared';
import { Button, Card, Field, InfoTip, Input, Notice, Spinner, fmtDate, formatUsd } from '@rydres/ui';
import { riderPayments, riderStatementLink, type RiderPayments } from '../lib/passenger.js';
import { Shell, useRiderGate } from './Passenger.js';

const MODES = ['month', 'year', 'custom'] as const;
type Mode = (typeof MODES)[number];
const SECTION = 'px-1 text-xs font-bold uppercase tracking-wide text-muted';
const show = (ymd: string, opts: Intl.DateTimeFormatOptions) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', ...opts });
const monthName = (key: string) => show(`${key}-15`, { month: 'long', year: 'numeric' });

/** rydres.com/me/payments: what the rider has paid, by ride and date, with totals and statements. */
export function PaymentsPage() {
  const { p, gate } = useRiderGate('Payments', `Sign in with the mobile number you book with to see what you have paid your ${BRAND.name} drivers.`);
  const today = localParts(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone).ymd;
  const [mode, setMode] = useState<Mode>('month');
  const [anchor, setAnchor] = useState(today);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [data, setData] = useState<RiderPayments | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signedIn = Boolean(p.me);
  const period = mode === 'month' ? monthRange(anchor) : mode === 'year' ? yearRange(anchor) : custom;
  const ready = Boolean(period.from && period.to && period.from <= period.to);

  useEffect(() => {
    if (!signedIn || !ready) return;
    setData(null);
    riderPayments(period).then(setData, () => setData({ records: [], totals: { paidCents: 0, rides: 0, byMonth: [], byYear: [] } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, ready, period.from, period.to]);
  if (gate) return gate;

  const download = async (format: 'pdf' | 'csv') => {
    setError(null);
    // The tab has to open during the tap, or a pop-up blocker stops it; it goes to the file once the link is ready.
    const tab = window.open('', '_blank');
    try {
      const { url } = await riderStatementLink({ ...period, format });
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      tab?.close();
      setError(err instanceof ApiError ? err.message : 'Could not make the statement.');
    }
  };
  const title = mode === 'month' ? show(anchor, { month: 'long', year: 'numeric' }) : mode === 'year' ? anchor.slice(0, 4) : 'Your dates';
  const months = data ? [...new Set(data.records.map((r) => r.day.slice(0, 7)))] : [];

  return (
    <Shell>
      <h1 className="flex items-center gap-2 text-2xl font-black">
        Payments
        <InfoTip label="Payments">Every card payment, and what your drivers record as paid to them directly. Each driver is the seller of their own rides; {BRAND.name} is not.</InfoTip>
      </h1>
      {error && <Notice tone="danger">{error}</Notice>}
      <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
        {MODES.map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} className={`flex-1 rounded-full px-3 py-1.5 capitalize ${mode === m ? 'bg-card shadow-card' : 'text-muted'}`}>{m}</button>
        ))}
      </div>
      {mode === 'custom' ? (
        <div className="grid grid-cols-2 gap-2">
          <Field label="From"><Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} /></Field>
          <Field label="To"><Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} /></Field>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => setAnchor(addMonthsYmd(anchor, mode === 'month' ? -1 : -12))} aria-label={`Earlier ${mode}`} className="h-11 w-11 rounded-full bg-card2 font-bold">‹</button>
          <div className="text-center font-black">{title}</div>
          <button type="button" onClick={() => setAnchor(addMonthsYmd(anchor, mode === 'month' ? 1 : 12))} aria-label={`Later ${mode}`} className="h-11 w-11 rounded-full bg-card2 font-bold">›</button>
        </div>
      )}

      {!ready ? (
        <p className="px-1 text-sm text-ink2">Pick the first and last date.</p>
      ) : data === null ? (
        <Spinner />
      ) : (
        <>
          <Card>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-ink2">Paid in this period</span>
              <span className="text-2xl font-black">{formatUsd(data.totals.paidCents)}</span>
            </div>
            {data.totals.byMonth.length > 1 && (
              <dl className="mt-3 space-y-1 border-t border-line pt-3 text-sm">
                {data.totals.byMonth.map((b) => (
                  <div key={b.key} className="flex justify-between gap-3"><dt className="text-ink2">{monthName(b.key)}</dt><dd className="font-bold">{formatUsd(b.paidCents)}</dd></div>
                ))}
                {data.totals.byYear.length > 1 && data.totals.byYear.map((b) => (
                  <div key={b.key} className="flex justify-between gap-3 font-black"><dt>Year {b.key}</dt><dd>{formatUsd(b.paidCents)}</dd></div>
                ))}
              </dl>
            )}
            <div className="mt-3 flex gap-2">
              <Button small variant="ghost" className="flex-1" disabled={!data.records.length} onClick={() => void download('pdf')}>Statement (PDF)</Button>
              <Button small variant="ghost" className="flex-1" disabled={!data.records.length} onClick={() => void download('csv')}>Spreadsheet (CSV)</Button>
            </div>
          </Card>

          {data.records.length === 0 ? (
            <p className="px-1 text-sm text-ink2">No payments in this period.</p>
          ) : (
            months.map((m) => (
              <section key={m} className="space-y-2" aria-label={monthName(m)}>
                <h2 className={SECTION}>{monthName(m)}</h2>
                {data.records.filter((r) => r.day.slice(0, 7) === m).map((r) => (
                  <div key={r.id} className="rounded-2xl border border-line bg-card p-4">
                    <Link to={`/b/${r.manageToken}`} className="block space-y-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-bold">{fmtDate(r.date, r.driver.timezone)}</span>
                        <span className="text-lg font-black">{formatUsd(r.amountCents)}</span>
                      </div>
                      <div className="line-clamp-2 text-sm text-ink2">{r.pickupAddress} → {r.dropoffAddress}</div>
                      <div className="text-xs text-muted">{r.driver.name ?? r.driver.legalName} · {r.label}</div>
                    </Link>
                    {r.receiptUrl && <a href={r.receiptUrl} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs font-bold text-accent-text underline">Card receipt</a>}
                  </div>
                ))}
              </section>
            ))
          )}
        </>
      )}
    </Shell>
  );
}
